import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, Trash2, Plus } from 'lucide-react';

export const CustomizationFieldRow = ({
  sectionId,
  option,
  value,
  onValueChange,
  formatCurrency,
  showDelete = false,
  onDelete,
  showCustomBadge = false,
  onFocusField,
}) => {
  const handleUpdate = (nextValue) => onValueChange?.(option.option_id, nextValue);

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-3"
      data-testid={`customization-field-row-${option.option_id}`}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <label
            className="text-sm font-medium text-slate-800 flex items-center gap-2"
            data-testid={`customization-field-label-${option.option_id}`}
          >
            {option.name}
            {option.is_required && (
              <Badge variant="destructive" className="text-[10px]" data-testid={`customization-required-badge-${option.option_id}`}>
                Required
              </Badge>
            )}
            {showCustomBadge && option.is_custom && (
              <Badge variant="secondary" className="text-[10px]" data-testid={`customization-custom-badge-${option.option_id}`}>
                Custom
              </Badge>
            )}
            {(option.price_impact || 0) > 0 && (
              <Badge variant="outline" className="text-[10px]" data-testid={`customization-price-impact-badge-${option.option_id}`}>
                +{formatCurrency?.(option.price_impact) || option.price_impact}
              </Badge>
            )}
          </label>
          {!!option.placeholder && (
            <p className="mt-0.5 text-xs text-slate-500" data-testid={`customization-placeholder-${option.option_id}`}>
              {option.placeholder}
            </p>
          )}
        </div>

        {showDelete && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 text-red-500 hover:bg-red-50 hover:text-red-700"
            onClick={() => onDelete?.(sectionId, option.option_id, option)}
            data-testid={`customization-delete-field-button-${option.option_id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {option.option_type === 'checkbox' ? (
        <div className="flex items-center gap-2" data-testid={`customization-checkbox-wrapper-${option.option_id}`}>
          <Checkbox
            checked={value === 'true'}
            onCheckedChange={(checked) => handleUpdate(checked ? 'true' : '')}
            data-testid={`customization-checkbox-${option.option_id}`}
          />
          <span className="text-sm text-slate-700">{option.placeholder || 'Yes'}</span>
        </div>
      ) : option.option_type === 'textarea' ? (
        <Textarea
          value={value || ''}
          onFocus={() => onFocusField?.(option.option_id)}
          onChange={(e) => handleUpdate(e.target.value)}
          placeholder={option.placeholder || `Enter ${String(option.name || '').toLowerCase()}`}
          rows={3}
          data-testid={`customization-textarea-${option.option_id}`}
        />
      ) : (
        <div className="flex gap-2" data-testid={`customization-input-wrapper-${option.option_id}`}>
          <Input
            value={value || ''}
            onFocus={() => onFocusField?.(option.option_id)}
            onChange={(e) => handleUpdate(e.target.value)}
            placeholder={option.placeholder || `Enter ${String(option.name || '').toLowerCase()}`}
            className="h-10"
            data-testid={`customization-input-${option.option_id}`}
          />

          {(option.option_type === 'select' || option.option_type === 'radio') && (option.options || []).length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0"
                  data-testid={`customization-options-dropdown-trigger-${option.option_id}`}
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[220px] max-h-[280px] overflow-y-auto" data-testid={`customization-options-dropdown-${option.option_id}`}>
                {option.options.map((opt) => (
                  <DropdownMenuItem
                    key={`${option.option_id}-${opt}`}
                    onClick={() => handleUpdate(opt)}
                    data-testid={`customization-option-item-${option.option_id}-${String(opt).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {opt}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
    </div>
  );
};

export const SectionHeaderAction = ({ onClick, testId = 'customization-add-custom-field-button' }) => (
  <Button
    type="button"
    variant="outline"
    size="sm"
    className="h-7 text-xs"
    onClick={onClick}
    data-testid={testId}
  >
    <Plus className="mr-1 h-3 w-3" />
    Add Custom Field
  </Button>
);
